/**
 * Shows banner about subscription status on a team site.
  *
 * Business logic:
 *
 * When a subscription invoice isn’t paid, or credit card payment fails, the
 * subscription is marked in stripe as past due. We should notify relevant users
 * that this has happened and give them a 30-day grace period to pay the invoice or update their
 * credit card info (this grace period is only in stripe, Grist doesn't manage it).
 *
 * That warning is this banner's whole job. Once the site is actually held read-only, whether
 * by this subscription or by anything else, SiteReadOnlyBanner says so and says what to do
 * about it, and this one stops: two banners disagreeing about whether documents can still be
 * edited would be worse than either alone.
 *
 * Notice: this is SAAS only code.
 *
 * Preconditions to show the banner at all:
 * - this must be a team site - for now personal sites are free and even if they are readonly or in bad
 *   shape - we don't show anything (at least not in this banner).
 * - user is not a viewer.
 * - the site is not already read-only (org.readOnlyReason).
 */

import { Banner, buildBannerMessage } from "app/client/components/Banner";
import { makeT } from "app/client/lib/localization";
import { inlineMarkdown } from "app/client/lib/markdown";
import { AppModel } from "app/client/models/AppModel";
import { urlState } from "app/client/models/gristUrlState";

import { Disposable, dom, DomElementArg, makeTestId } from "grainjs";

const t = makeT("SubscriptionBanner");
const testId = makeTestId("test-subscription-banner-");

export class SubscriptionBanner extends Disposable {
  constructor(private _app: AppModel) {
    super();
  }

  public buildDom() {
    const org = this._app.currentOrg;
    // Only for team sites. Personal sites are free, and say nothing here even when in a bad way.
    if (!this._app.isTeamSite || !org?.billingAccount) { return null; }

    // Viewers don't see subscription banners at all.
    if (!this._app.isOwnerOrEditor()) { return null; }

    // Nothing to warn about once the site is already read-only, whatever put it there.
    if (org.readOnlyReason) { return null; }

    if (org.billingAccount.status?.stripeStatus !== "past_due") { return null; }

    const billingUrl = urlState().makeUrl({ billing: "billing" });
    // How many days until site goes into read-only mode (for past_due status), we assume that 30
    // days after end of current period (this is hardcoded this information is only in stripe).
    const endOfPeriod = org.billingAccount.status.currentPeriodEnd;
    const endOfPeriodPlus30Days = endOfPeriod ? movedDate(date(endOfPeriod), 30) : null;
    const daysCalculated = endOfPeriodPlus30Days ? dayDiff(endOfPeriodPlus30Days, date(new Date())) : 30;
    const days = daysCalculated > 0 ? daysCalculated : 0; // don't show negative days, should not happen.

    const message = this._app.isOwner() ?
      inlineMarkdown(t(
        "Your team's subscription plan is past due. Please [pay the latest invoice]({{billingUrl}}) \
         to maintain an active subscription. Your team site will become read-only in {{days}} days.",
        { billingUrl, days },
      )) :
      t("Your team's subscription plan is past due. Please contact the site owner to update payment.");

    return dom.create(Banner, {
      content: buildBannerMessage(
        message,
        testId("text"),
        hidden(days, testId("days")),
        hidden("warning", testId("style")),
      ),
      style: "warning",
      showCloseButton: false,
    });
  }
}

// Helper function to create hidden input with value, used for testing to easily get the state of the banner.
function hidden(value: any, ...args: DomElementArg[]) {
  return dom("input", { type: "hidden", value: String(value) }, ...args);
}

// Some date helpers to avoid the need of importing moment to this bundle.
function date(d: string | Date): Date {
  const clone = ((d instanceof Date) ? new Date(d.getTime()) : new Date(d));
  clone.setUTCHours(0, 0, 0, 0);
  return clone;
}

function movedDate(d: Date, days: number): Date {
  const result = new Date(d);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

function dayDiff(date1: Date, date2: Date): number {
  return Math.floor((date1.getTime() - date2.getTime()) / (24 * 60 * 60 * 1000));
}
